import { useWhiteboardNavigation } from "@src/components/board/use-whiteboard-navigation";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { parseAppRoute } from "../parse-app-route";
import { resolveAppNavigatePath } from "../resolve-app-navigate-path";
import type {
	AppAgentActions,
	AppAgentNavigateAppResult,
} from "./app-agent-context";

export function useAppAgentActions(): AppAgentActions {
	const location = useLocation();
	const navigate = useNavigate();
	const { navigateToWhiteboard } = useWhiteboardNavigation();

	const getCurrentRoute = useCallback<AppAgentActions["getCurrentRoute"]>(
		async () => parseAppRoute(location.pathname, location.search),
		[location.pathname, location.search],
	);

	const navigateApp = useCallback<AppAgentActions["navigateApp"]>(
		async (input) => {
			const resolved = resolveAppNavigatePath(input);
			if ("error" in resolved) {
				return { ok: false, error: resolved.error };
			}

			if (input.destination === "whiteboard" && resolved.whiteboardId) {
				navigateToWhiteboard(resolved.whiteboardId);
			} else {
				navigate(resolved.pathname);
			}

			const result: AppAgentNavigateAppResult = {
				ok: true,
				destination: input.destination,
				pathname: resolved.pathname,
				...(resolved.whiteboardId
					? { whiteboardId: resolved.whiteboardId }
					: {}),
				...(resolved.cardId ? { cardId: resolved.cardId } : {}),
			};
			return result;
		},
		[navigate, navigateToWhiteboard],
	);

	return useMemo(
		() => ({
			getCurrentRoute,
			navigateApp,
		}),
		[getCurrentRoute, navigateApp],
	);
}
